import { useEffect, useState } from 'react';
import { Note01Icon, ViewOffSlashIcon, LaptopPhoneSyncIcon } from '@hugeicons/core-free-icons';
import { ToggleRow } from '../ui/ToggleRow';

export interface ClientReviewDefaultsValues {
  clientReviewWelcomeEnabled?: boolean;
  clientReviewWelcomeNote?: string | null;
  clientReviewPrivateComments?: boolean;
  clientReviewAllowResponsive?: boolean;
}

interface ClientReviewDefaultsProps {
  project: ClientReviewDefaultsValues;
  onSave: (patch: ClientReviewDefaultsValues) => void;
  disabled?: boolean;
}

export const WELCOME_NOTE_MAX = 500;
export const WELCOME_NOTE_PLACEHOLDER = 'Focus on the hero and pricing sections. Comment on anything that looks off.';

/**
 * Project Settings > Sharing > "Client reviews": the defaults every new client
 * link starts from (docs/prototypes/guest-review-toggles/index.html). Each link
 * keeps its own copy, set in the create modal's Guest options.
 */
export function ClientReviewDefaults({ project, onSave, disabled }: ClientReviewDefaultsProps) {
  const welcomeOn = project.clientReviewWelcomeEnabled === true;
  const privateOn = project.clientReviewPrivateComments === true;
  const responsiveOn = project.clientReviewAllowResponsive !== false;
  const savedNote = project.clientReviewWelcomeNote ?? '';
  const [note, setNote] = useState(savedNote);
  useEffect(() => setNote(savedNote), [savedNote]);

  return (
    <div>
      <h3 className="section-label mb-3">Client links</h3>
      <div className="space-y-4">
        <ToggleRow
          icon={Note01Icon}
          label="Welcome note"
          hint="Shown at the top of the comments panel the first time a guest opens a link."
          checked={welcomeOn}
          disabled={disabled}
          onChange={() => onSave({ clientReviewWelcomeEnabled: !welcomeOn })}
        >
          <textarea
            aria-label="Welcome note text"
            className="input input-lg w-full h-auto py-2"
            rows={3}
            maxLength={WELCOME_NOTE_MAX}
            placeholder={WELCOME_NOTE_PLACEHOLDER}
            value={note}
            disabled={disabled}
            onChange={(e) => setNote(e.target.value)}
            onBlur={() => { if (note !== savedNote) onSave({ clientReviewWelcomeNote: note.trim() || null }); }}
          />
        </ToggleRow>
        <ToggleRow
          icon={ViewOffSlashIcon}
          label="Keep guest comments private"
          hint="Each guest sees only their own comments. Your team still sees all of them."
          checked={privateOn}
          disabled={disabled}
          onChange={() => onSave({ clientReviewPrivateComments: !privateOn })}
        />
        <ToggleRow
          icon={LaptopPhoneSyncIcon}
          label="Allow responsive views"
          hint="Clients can switch between desktop, tablet, and mobile."
          checked={responsiveOn}
          disabled={disabled}
          onChange={() => onSave({ clientReviewAllowResponsive: !responsiveOn })}
        />
      </div>
    </div>
  );
}
